import { EventEmitter, Inject, Injectable, Output } from '@angular/core';
import { TriggerbeeApi, TriggerbeeRequester } from './../core/triggerbee/triggerbee-requester.service';
import { Logger, LoggerFactory } from './../core/logger.service';
import { Router } from '@angular/router';
import { VisitsSelectionReuqest } from 'app/dashboard/leads/visits-request';
import { Visit, VisitsResponse } from 'app/visits/visits-response';
import { KeyValuePair } from 'app/shared/key-value-pair';
import { QueryContext } from '../search/query-context';
import { SearchService } from '../search/search.service';
import { IMtr } from 'app/core/tracker/mtr.interface';
import { WidgetService } from '../shared/services/widget.service';
import { TranslateService } from '@ngx-translate/core';
import { Widget, WidgetSimpleInfo } from '../campaign/widgets.model';
import { EventTypeHash } from './search-result/search-result.component';

export class UpdateHeaderResponse {
  constructor(
    public header: string,
    public totalCount: number,
  ) {}
}

@Injectable()
export class VisitsService {
  private readonly logger: Logger;
  private readonly eventTypeDictionary: EventTypeHash;
  // Page goals are stored as visitedpage-{name}; the prefix is internal.
  private static readonly pageGoalPrefix = 'visitedpage-';
  // Action goals are oc_action_{widgetId}_{stateId}[_{componentId}], so only the leading widget id is captured.
  private readonly goalEventRegex = /oc_(?:click|submit|discount|interaction|action)_\d*/g;

  @Output() public updateList = new EventEmitter<Visit[]>();
  @Output() public updateListHeader = new EventEmitter<string>();
  @Output() public updateListSubHeader = new EventEmitter<UpdateHeaderResponse>();
  @Output() public disablePageChange = new EventEmitter<boolean>();
  @Output() public resetPagination = new EventEmitter();

  public queryContext = new QueryContext();
  public visits: Visit[];
  private pageSize = 50;
  private visitsTranslationKey = 'generic.visits';
  private allWidgets: Array<WidgetSimpleInfo>;

  constructor(
    loggerFactory: LoggerFactory,
    private readonly requester: TriggerbeeRequester,
    protected readonly router: Router,
    private readonly searchService: SearchService,
    private readonly widgetsService: WidgetService,
    private translateService: TranslateService,
    @Inject('Mtr') private readonly mtr: IMtr,
  ) {
    this.logger = loggerFactory.get('VisitsService');
    this.visits = [];
    this.eventTypeDictionary = {
      click: this.translateService.instant('visits.goalEvents.clickThrough'),
      submit: this.translateService.instant('visits.goalEvents.formSubmission'),
      discount: this.translateService.instant('visits.goalEvents.discount'),
      interaction: this.translateService.instant('visits.goalEvents.componentAction'),
      action: this.translateService.instant('visits.goalEvents.componentAction'),
    };
  }

  public getGoalDisplayName(goal: string, allWidgets: Array<WidgetSimpleInfo>, addBold = true): string {
    let goalName = this.getGoalName(goal, allWidgets);
    if (!addBold) {
      goalName = this.stripHtml(goalName);
    }
    return goalName;
  }

  private getGoalName(goal: string, allWidgets: Array<WidgetSimpleInfo>): string {
    if (goal?.startsWith(VisitsService.pageGoalPrefix)) {
      return goal.slice(VisitsService.pageGoalPrefix.length);
    }
    if (allWidgets && goal) {
      const matches = goal.match(this.goalEventRegex);
      if (matches) {
        matches.forEach((matchedText) => {
          const [oc, eventType, widgetId] = matchedText.split('_');
          if (widgetId && eventType) {
            const relatedWidget = allWidgets.find((widget) => +widgetId === widget.id);
            if (relatedWidget) {
              goal = relatedWidget.campaignName
                ? this.eventTypeDictionary[eventType] + ` <strong>"${relatedWidget.campaignName}"</strong>`
                : this.eventTypeDictionary[eventType] + ' ' + `<strong>widget ${widgetId}</strong>`;
            } else {
              switch (eventType) {
                case 'click':
                  goal = this.translateService.instant('visits.goalEvents.clickThroughRemoved', { widgetId: widgetId });
                  break;
                case 'submit':
                  goal = this.translateService.instant('visits.goalEvents.formSubmissionRemoved', {
                    widgetId: widgetId,
                  });
                  break;
                case 'discount':
                  goal = this.translateService.instant('visits.goalEvents.discountRemoved', { widgetId: widgetId });
                  break;
                case 'interaction':
                case 'action':
                  goal = this.translateService.instant('visits.goalEvents.componentActionRemoved', {
                    widgetId: widgetId,
                  });
                  break;
                default:
              }
            }
          }
        });
      } else {
        const currentWidget = allWidgets.filter((item) => item.id === Number(goal));
        if (currentWidget && currentWidget.length) {
          goal =
            this.translateService.instant('insights.live.displayedCampaign') +
            ':' +
            ' ' +
            currentWidget[0].campaignName;
        }
      }
    }
    return goal;
  }

  private stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  public async getWidgetsList(): Promise<Array<Widget>> {
    const result = await this.widgetsService.getAllWidgets().toPromise();
    this.allWidgets = result;
    return result;
  }

  public async getWidgetsSimpleInfoList(): Promise<Array<WidgetSimpleInfo>> {
    const result = await this.widgetsService.getAllWidgetsSimpleInfo().toPromise();
    this.allWidgets = result;
    return result;
  }

  public async updateVisitsList(visits: Visit[]) {
    const allWidgets = this.allWidgets;
    const eventToMatchGoalRegex = /<strong>(.*?)<\/strong>/g;
    if (visits?.length) {
      visits.forEach((visit) => {
        if (visit.events) {
          visit.events.forEach((event) => {
            if (event.icon === 'medal') {
              let goalConverted = this.translateService.instant('visits.goalEvents.completedGoals') + ' ';
              let goalMatches: Array<string>;
              goalMatches = event.text.match(eventToMatchGoalRegex);
              if (goalMatches) {
                // Removing duplicates
                goalMatches = this.removeDuplicates(goalMatches);
                for (let i = 0; i < goalMatches.length; i++) {
                  const goalWithWidgetIdMatches = goalMatches[i].match(this.goalEventRegex);
                  if (goalWithWidgetIdMatches) {
                    goalMatches[i] = this.getGoalDisplayName(goalWithWidgetIdMatches[0], allWidgets);
                  }
                  // Populate final string
                  goalConverted += goalMatches[i] + (i < goalMatches.length - 1 ? ', ' : '.');
                }
              }
              event.text = goalConverted;
            } else if (event.icon === 'pie-chart') {
              const eventText = event.text.split('<strong>').pop().split('</strong>')[0];
              const desc = this.translateService.instant('visits.goalEvents.mostInterested');
              event.text = desc + '<strong>' + eventText + '</strong>';
            } else if (event.text.startsWith('Came through')) {
              const eventText = event.text.split('<strong>').pop().split('</strong>')[0];
              const desc = this.translateService.instant('visits.goalEvents.cameThrough');
              event.text = desc + '<strong>' + eventText + '</strong>';
            }
          });
        }
        if (visit.labels) {
          visit.labels.forEach((label) => {
            if (
              label.title === 'Customer' ||
              label.title === 'Competitor' ||
              label.title === 'Partner' ||
              label.title === 'Prospect' ||
              label.title === 'Isp' ||
              label.title === 'Supplier' ||
              label.title === 'Hot' ||
              label.title === 'Uninteresting' ||
              label.title === 'Coworker'
            ) {
              label.title = this.translateService.instant(`visits.labels.${label.title.toLocaleLowerCase()}`);
            }
          });
        }
      });
    }
    await this.updateList.emit(visits);
  }

  private removeDuplicates(array: Array<string>): Array<string> {
    const s = new Set(array);
    const it = s.values();
    return Array.from(it);
  }

  public async getVisitsByQueryContext(queryContext: QueryContext): Promise<VisitsResponse> {
    this.queryContext = JSON.parse(JSON.stringify(queryContext));

    // Execute query
    const response = await this.requester.postAsync<VisitsResponse>(
      TriggerbeeApi.Gateway,
      '/visits/search',
      queryContext,
    );

    return response.data || new VisitsResponse();
  }

  public async getAdminVisitsByQueryContext(siteId: number, queryContext: QueryContext): Promise<VisitsResponse> {
    this.queryContext = JSON.parse(JSON.stringify(queryContext));

    // Execute query
    const response = await this.requester.postAsync<VisitsResponse>(
      TriggerbeeApi.Gateway,
      `admin/visits/search/${siteId}`,
      queryContext,
    );

    return response.data || new VisitsResponse();
  }

  private async countVisitsByQueryContext(queryContext: QueryContext): Promise<number> {
    this.queryContext = JSON.parse(JSON.stringify(queryContext));

    // Execute query
    const response = await this.requester.postAsync<number>(TriggerbeeApi.Gateway, '/visits/count', queryContext);
    return response.data;
  }

  public async updateCount(queryContext: QueryContext, total: number): Promise<number> {
    if (queryContext.take === total) {
      return await this.countVisitsByQueryContext(queryContext);
    }
    return total;
  }

  public async checkForNewVisits(queryContext: QueryContext): Promise<boolean> {
    const response = await this.requester.postAsync<boolean>(TriggerbeeApi.Gateway, '/visits/new', queryContext);
    return response.data || false;
  }

  public async getNextPage(page: number, total: number) {
    const request = this.getVisitsQueryContext();
    request.take = this.pageSize;
    request.skip = this.pageSize * (page - 1);
    delete request.orderBy;
    const result = await this.getVisitsByQueryContext(request);
    result.total = total;
    this.visits = this.visits.concat(result.visits);
    await this.updateVisitsList(this.visits);
    await this.updateListSubHeader.emit(
      new UpdateHeaderResponse(
        await this.searchService.formatsubListHeader(
          this.visits.length,
          result.total,
          this.visitsTranslationKey,
          this.pageSize,
        ),
        result.total,
      ),
    );
    this.checkPagination(result.total);
  }

  public getVisitsQueryContext() {
    return this.queryContext;
  }

  public setQueryContext(context) {
    this.queryContext = context;
  }

  public async refreshVisits(response: VisitsResponse, filterName: string, queryContext: QueryContext) {
    this.mtr.log(window.location.pathname, `Searched for '${filterName}' on Visits`, 'event');

    if (response.visits) {
      this.visits = response.visits;
    }
    this.resetPagination.emit();
    await this.updateVisitsList(response.visits);
    await this.updateListHeader.emit(filterName);

    // update count
    response.total = await this.updateCount(queryContext, response.total);

    await this.updateListSubHeader.emit(
      new UpdateHeaderResponse(
        await this.searchService.formatsubListHeader(
          this.pageSize,
          response.total,
          this.visitsTranslationKey,
          this.pageSize,
        ),
        response.total,
      ),
    );
    this.checkPagination(response.total);
  }

  public async checkPagination(total: number) {
    // -1 is the value returned for Cosmos DB accounts when impossible to count records
    if ((total <= this.pageSize && total !== -1) || total === this.visits.length) {
      this.disablePageChange.emit(true);
    } else {
      this.disablePageChange.emit(false);
    }
  }

  public getStringOperators(): KeyValuePair<number>[] {
    return Object.values(StringOperators)
      .filter((x) => typeof x === 'string')
      .map((key: string) => ({
        key,
        value: StringOperators[key],
      }));
  }

  public getNumberOperators(): KeyValuePair<number>[] {
    return Object.values(NumberOperators)
      .filter((x) => typeof x === 'string')
      .map((key: string) => ({
        key,
        value: NumberOperators[key],
      }));
  }
}

enum StringOperators {
  Equals = 0,
  NotEquals = 1,
  StartsWith = 10,
  EndsWith = 11,
  Contains = 2,
  Any = 12,
}

enum NumberOperators {
  Equals = 0,
  LessThan = 7,
  MoreThan = 6,
}
